import tokenStorage from "./components/auth-context-provider/tokenStorage";
import { 
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  from
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";

// Función para crear el upload link dinámicamente usando apollo-upload-client
const createUploadLinkAsync = async () => {
  try {
    const { default: createUploadLink } = await import("apollo-upload-client/createUploadLink.mjs");
    console.log("✅ apollo-upload-client loaded successfully");
    return createUploadLink;
  } catch (error) {
    // Fallback: algunos Samsung Internet no soportan import() de .mjs correctamente
    console.warn("⚠️ apollo-upload-client dynamic import failed, using HttpLink fallback:", error);
    const { HttpLink } = await import("@apollo/client");
    return (options: any) => new HttpLink(options);
  }
};

export const createApolloClient = async () => {
  const graphqlUri = import.meta.env.VITE_GRAPHQL_URI || import.meta.env.REACT_APP_GRAPHQL_URI || "/graphql";
  
  console.log(`🌐 Apollo Client connecting to: ${graphqlUri}`);

  // Crear el upload link de forma asíncrona
  const createUploadLink = await createUploadLinkAsync();
  
  // Configure upload link with explicit options
  const uploadLink = createUploadLink({
    uri: graphqlUri,
    headers: {
      'Apollo-Require-Preflight': 'true',
    },
    fetchOptions: {
      method: 'POST',
    },
  });

  // Create auth link that preserves upload functionality
  const authLink = new ApolloLink((operation, forward) => {
    const token = tokenStorage.getToken();
    
    if (token && operation.operationName !== "Login") {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        }
      }));
    }
    
    return forward(operation);
  });

  const client = new ApolloClient({
    link: from([
      onError(({ graphQLErrors, networkError, operation }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(`❌ GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`);
          });
          
          // Public operations that should not redirect to login
          const publicOperations = [
            'ValidatePasswordToken',
            'SetupCoachPassword',
            'PublicMatch',
            // Add other public operations here
          ];
          
          const isPublicOperation = publicOperations.includes(operation.operationName || '');
          
          const foundError = graphQLErrors.find((err) =>
            Boolean(err.extensions && err.extensions.code === "UNAUTHENTICATED")
          );
          
          if (foundError && !isPublicOperation) {
            console.warn('🔐 Authentication error detected, redirecting to login');
            tokenStorage.clearToken();
            document.location.href = "/login";
          }
        }
        
        if (networkError) {
          console.error(`❌ Network error:`, networkError);
        }
      }),
      authLink,
      uploadLink,
    ]),
    cache: new InMemoryCache(),
    resolvers: {
      Mutation: {
        updateNetworkStatus: (_, { isConnected }, { cache }) => {
          cache.writeData({ data: { isConnected } });
          return null;
        },
      },
    },
  });

  return client;
};
